import styles from "./page.module.scss";
import type {
    IProps,
    IPageHeaderProps,
    PageHeaderFactory,
    Factory,
    PageContentFactory,
    IPageContentProps,
    PageNavbarFactory,
    IPageNavbarProps,
    PageContentHeaderFactory,
    IPageContentHeaderProps,
} from "./page.types";
import { Box } from "../../box";
import { componentWrapper, polymorphicComponentWrapper } from "../../../utilities";
import { AppShell } from "../../app-shell";
import { Group } from "../../group";
import { Title } from "../../title";
import classnames from "classnames";
import { Stack } from "../../stack";
import { PropsWithChildren } from "react";

/**
 * Page component for page-level layout and structure.
 * @param {string} props.name - The name identifier for the page.
 * @param {React.ReactNode} props.children - Content to display in the page.
 * @param {string} props.className - Additional CSS class names.
 * @returns {JSX.Element} The Page component.
 */
const Page = polymorphicComponentWrapper<Factory, IProps>(
    "Page",
    ({ name, children, className, ...otherProps }, ref) => {
        return (
            <Box ref={ref} className={classnames("page", name, className, styles["page"])} {...otherProps}>
                {children}
            </Box>
        );
    }
);

/**
 * Page header component for displaying page titles and header content.
 * @param {string | React.ReactNode} props.title - The title of the page.
 * @param {React.ReactNode} props.children - Additional content to display in the header.
 * @param {string} props.className - Additional CSS class names.
 * @returns {JSX.Element} The PageHeader component.
 */
const PageHeader = polymorphicComponentWrapper<PageHeaderFactory, IPageHeaderProps>(
    "PageHeader",
    ({ title, children, className, ...otherProps }, ref) => {
        return (
            <AppShell.Header>
                <Group
                    justify="space-between"
                    h="100%"
                    align="center"
                    px="lg"
                    wrap="nowrap"
                    ref={ref}
                    className={`${className} ${styles["header"]}`}
                    {...otherProps}
                >
                    {typeof title === "string" ? (
                        <Title order={3} lineClamp={1} className={styles["title"]}>
                            {title}
                        </Title>
                    ) : (
                        <Box miw={0} flex={1}>
                            {title}
                        </Box>
                    )}
                    {children}
                </Group>
            </AppShell.Header>
        );
    }
);

/**
 * Page navbar component for displaying navigation within a page.
 * @param {string} props.className - Additional CSS class names.
 * @returns {JSX.Element} The PageNavbar component.
 */
const PageNavbar = polymorphicComponentWrapper<PageNavbarFactory, IPageNavbarProps>(
    "PageNav",
    ({ className, disableScroll, ...otherProps }, ref) => {
        return (
            <AppShell.SecondaryNavbar disableScroll={disableScroll}>
                <Stack
                    data-disable-scroll={disableScroll}
                    className={`${className} ${styles["nav"]}`}
                    {...otherProps}
                />
            </AppShell.SecondaryNavbar>
        );
    }
);

/**
 * Page content component for displaying the main content area of a page.
 * @param {React.ReactNode} props.children - Content to display in the page.
 * @param {string} props.className - Additional CSS class names.
 * @param {string} props.height - Height configuration for the content area.
 * @param {string} props.width - Width configuration for the content area.
 * @param {object} props.appShellMainProps - Additional props for the AppShell.Main component.
 * @returns {JSX.Element} The PageContent component.
 */
const PageContent = polymorphicComponentWrapper<PageContentFactory, IPageContentProps>(
    "PageContent",
    ({ children, className, height, width, appShellMainProps, ...otherProps }, ref) => {
        return (
            <AppShell.Main
                {...appShellMainProps}
                display="flex"
                {...(height === "full" ? { mah: "100dvh" } : {})}
                {...(width === "full" ? { maw: "unset", style: { overflowX: "scroll" } } : {})}
            >
                <Stack ref={ref} flex={1} miw={0} className={classnames(className, styles["content"])} {...otherProps}>
                    {children}
                </Stack>
            </AppShell.Main>
        );
    }
);

/**
 * Page content header title component for displaying styled titles within page content headers.
 * @param {React.ReactNode} props.children - The title content to display.
 * @returns {JSX.Element} The PageContentHeaderTitle component.
 */
const PageContentHeaderTitle = ({ children }: PropsWithChildren) => {
    return (
        <Title order={3} lineClamp={1} flex={1} className={styles["title"]}>
            {children}
        </Title>
    );
};

/**
 * Page content header component for displaying section titles within page content.
 * @param {React.ReactNode} props.title - The title to display.
 * @param {React.ReactNode} props.children - Additional content to display next to the title.
 * @returns {JSX.Element} The PageContentHeader component.
 */
const PageContentHeader = componentWrapper<PageContentHeaderFactory, IPageContentHeaderProps>(
    "PageContentHeader",
    ({ title, children }: { title: React.ReactNode; children?: React.ReactNode }) => {
        return (
            <Group justify="space-between" wrap="nowrap">
                <PageContentHeaderTitle>{title}</PageContentHeaderTitle>
                {children}
            </Group>
        );
    }
);

/**
 * Page content body component for displaying the main body content.
 * @param {React.ReactNode} props.children - Content to display in the body.
 * @returns {JSX.Element} The PageContentBody component.
 */
const PageContentBody = ({ children }: PropsWithChildren<{}>) => {
    return <Box mt="lg">{children}</Box>;
};

PageContent.Header = PageContentHeader;
PageContent.Body = PageContentBody;
PageContent.Header.Title = PageContentHeaderTitle;
Page.Header = PageHeader;
Page.Content = PageContent;
Page.Navbar = PageNavbar;

export { Page, PageHeader, PageContent, PageNavbar, PageContentHeader, PageContentBody, PageContentHeaderTitle };
